import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { MobileMenu } from "@/components/MobileMenu";
import { WalletButton } from "@/components/WalletButton";
import { BuyButton, ChartButton, XIconButton } from "@/components/Buttons";
import { PROJECT_NAME, PROJECT_SUBTITLE } from "@/config/actra";

const LINKS = [
    { label: "Protocol", to: "/", hash: "#action-economy" },
    { label: "Tasks", to: "/tasks" },
    { label: "Agents", to: "/agents" },
    { label: "Proof", to: "/proof" },
    { label: "Reputation", to: "/reputation" },
    { label: "Docs", to: "/docs" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setMenuOpen(false), [location.pathname]);

    const handleProtocol = (e) => {
        e.preventDefault();
        setMenuOpen(false);
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => window.dispatchEvent(new CustomEvent("actra:goto", { detail: "#action-economy" })), 120);
        } else {
            window.dispatchEvent(new CustomEvent("actra:goto", { detail: "#action-economy" }));
        }
    };

    return (
        <>
            <header
                data-testid="navbar"
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "border-b border-white/[0.06] bg-[#040811]/85 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="shell flex h-16 items-center justify-between gap-4 md:h-20">
                    {/* LEFT — brand */}
                    <Link to="/" data-testid="nav-brand" className="flex items-center gap-3" aria-label={`${PROJECT_NAME} home`}>
                        <img
                            src="/actra-logo.png"
                            alt="ACTRA logo"
                            className="h-8 w-8 object-contain md:h-9 md:w-9"
                            style={{ mixBlendMode: "screen" }}
                        />
                        <span className="flex flex-col leading-none">
                            <span className="font-display text-lg font-bold tracking-[0.14em] text-ink">{PROJECT_NAME}</span>
                            <span className="hidden whitespace-nowrap font-mono text-[8px] uppercase tracking-[0.3em] text-mute lg:block">
                                ACTION VERIFICATION NETWORK
                            </span>
                        </span>
                    </Link>

                    {/* CENTER — nav */}
                    <nav aria-label="Primary" className="hidden items-center gap-7 lg:flex">
                        {LINKS.map((l) =>
                            l.hash ? (
                                <a
                                    key={l.label}
                                    href="/"
                                    onClick={handleProtocol}
                                    data-testid={`nav-link-${l.label.toLowerCase()}`}
                                    className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-mute transition-colors duration-300 hover:text-ink"
                                >
                                    {l.label}
                                </a>
                            ) : (
                                <NavLink
                                    key={l.label}
                                    to={l.to}
                                    data-testid={`nav-link-${l.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `font-mono text-[11px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 hover:text-ink ${
                                            isActive ? "text-ink" : "text-mute"
                                        }`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ),
                        )}
                    </nav>

                    {/* RIGHT — actions */}
                    <div className="flex items-center gap-2 md:gap-3">
                        <span className="hidden md:block">
                            <BuyButton compact />
                        </span>
                        <span className="hidden md:block">
                            <ChartButton compact />
                        </span>
                        <span className="hidden md:block">
                            <XIconButton />
                        </span>
                        <span className="hidden xl:block">
                            <WalletButton align="right" />
                        </span>
                        <Link
                            to="/tasks"
                            data-testid="nav-launch-app"
                            className="inline-flex items-center gap-2 bg-[#1677FF] px-4 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-[#3D8BFF] md:px-5 md:text-[11px]"
                        >
                            LAUNCH APP
                        </Link>
                        <button
                            data-testid="mobile-menu-toggle"
                            aria-label="Open menu"
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen(true)}
                            className="border border-white/10 p-2.5 text-ink transition-colors hover:border-[#1677FF] lg:hidden"
                        >
                            <Menu size={18} />
                        </button>
                    </div>
                </div>
            </header>

            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} onProtocol={handleProtocol} />
            <button
                data-testid="mobile-menu-close-hidden"
                aria-hidden="true"
                tabIndex={-1}
                className="hidden"
                onClick={() => setMenuOpen(false)}
            >
                <X size={18} />
            </button>
        </>
    );
};

export default Navbar;
